import type { BlobLike } from "@pdfking/compatibility"
import { FileCache } from "@pdfking/compatibility"
import type { AsyncSequence, Bytes, Time } from "@pdfking/tfunc"

export type FileBound = {
  readonly size: Bytes
  readonly modified: Time
  readonly stream: AsyncSequence<BlobLike>
}
import { BlobArray } from "@pdfking/compatibility"
import {
  FilterAsync,
  First,
  FullMethodDispatcher,
  Items,
  MapAsync,
  ResponseContentLength,
  ResponseHeader,
  SafePromise,
  StreamToIterable,
  UnixTime,
  Void,
} from "@pdfking/tfunc"

import { EngineError } from "../errors/EngineError"
import { FileContentGUID } from "../methods/FileContentGUID"
import { FileContentSize } from "../methods/FileContentSize"
import { ProgressFetch } from "../network/ProgressFetch"
import type { PdfEngine } from "../types/engine/PdfEngine"
import { ErrorName } from "../types/errors/ErrorName"
import type { GeneratedFile } from "../types/files/cached/GeneratedFile"
import type { RemoteFile } from "../types/files/cached/RemoteFile"
import type { VirtualFile } from "../types/files/VirtualFile"
import { VirtualFileType } from "../types/files/VirtualFileType"
import { TrackedTaskName } from "../types/tasks/tracking/TrackedTaskName"
import type { TrackingCallback } from "../types/tasks/tracking/TrackingCallback"

const dispatch = FullMethodDispatcher<
  [VirtualFile, PdfEngine, TrackingCallback],
  Promise<FileBound>
>({
  [VirtualFileType.REMOTE_FILE]: async (file, _engine, emit) => {
    for (const url of file.mirrors) {
      const { value: response } = await SafePromise(
        ProgressFetch(url, {
          download: e =>
            emit({
              name: TrackedTaskName.DOWNLOAD,
              ...e,
            }),
        })
      )
      if (response && response.ok) {
        const header = ResponseHeader(response, "last-modified")
        const modified = UnixTime(header ? new Date(header) : undefined)

        return {
          stream: MapAsync(
            chunk => BlobArray(chunk),
            StreamToIterable(response.body)
          ),
          size: ResponseContentLength(response),
          modified,
        }
      }
    }
    throw EngineError(ErrorName.FILE_NOT_FOUND)
  },

  [VirtualFileType.GENERATED_FILE]: async (file, engine, _emit) => {
    const blob = await FileCache(engine.config.file_cache).get(
      FileContentGUID(file)
    )

    if (!blob) {
      console.error("Cache miss for file", file)
      throw EngineError(ErrorName.CACHE_EXPIRED)
    }

    return {
      stream: [blob],
      size: blob.size as Bytes,
      modified: UnixTime(),
    }
  },

  [VirtualFileType.SINGLE_PAGE_IMAGE]: (file, engine, emit) =>
    dispatchWithFallback(file.content, engine, emit, () =>
      First(engine.RasterizePdf(file.source))
    ),

  [VirtualFileType.SINGLE_PAGE_PDF]: (file, engine, emit): Promise<FileBound> =>
    dispatchWithFallback(file.content, engine, emit, () => {
      if (file.source) {
        return First(
          FilterAsync(
            f => f.page_number === file.page_number,
            engine.SplitPdf(file.source)
          )
        )
      }
      return engine.PlotPdf(file.page, file.context, FileContentGUID(file))
    }),

  [VirtualFileType.MULTI_PAGE_PDF]: (file, engine, emit): Promise<FileBound> =>
    dispatchWithFallback(file.content, engine, emit, () =>
      engine.MergePdf(file.pages, FileContentGUID(file))
    ),

  [VirtualFileType.ZIP_FILE]: (file, engine, emit): Promise<FileBound> =>
    dispatchWithFallback(file.content, engine, emit, () =>
      engine.ZipFiles(Items(file.files), FileContentGUID(file))
    ),
})

const dispatchWithFallback = async (
  file: GeneratedFile | RemoteFile,
  engine: PdfEngine,
  emit: TrackingCallback = Void,
  fallback: () => Promise<VirtualFile>
): Promise<FileBound> => {
  const { value, error } = await SafePromise(dispatch(file, engine, emit))

  if (error) {
    const computed = await fallback()

    if (FileContentGUID(computed) === FileContentGUID(file)) {
      return dispatch(computed, engine, emit)
    }

    if (FileContentSize(computed) === FileContentSize(file)) {
      console.error("⚠️ sha256 failed, size passed", { computed, file })

      return dispatch(computed, engine, emit)
    }

    throw EngineError(ErrorName.CACHE_MISMATCH)
  }

  return value
}

export const RequestBlob = (
  engine: PdfEngine,
  file: VirtualFile,
  emit: TrackingCallback = Void
): Promise<FileBound> => dispatch(file, engine, emit)
